import R from "ramda";
import { store } from "store";
import {
  fetchAccountDetails,
  fetchLocales,
  putAccountSettings,
  putCurrencyLocaleTax,
  fetchPlans,
  putNewPlan,
  fetchBillingHistory,
  fetchBillingDetails,
  postBillingDetails,
  createUser,
  deleteUser,
  fetchAccountUsers,
  putDocReturn,
  fetchPendingPlan,
  billingRedirect
} from "api/account-settings";
import { setFlashMessageAction, setFlashGenericErrorAction } from "actions/app-actions";
import { isValidUser } from "utils/validators";
import { redirectToLoginAction } from "actions/oauth-actions";

export const SET_ACCOUNT_DETAILS = "actions/account-settings/set-account-details";
export const SET_LOCALES = "actions/account-settings/set-locales";
export const SET_PLAN_TYPE_DISPLAY = "actions/account-settings/set-plan-type-display";
export const SET_PLANS = "actions/account-settings/set-plans";
export const UPDATE_PLAN = "actions/account-settings/update-plan";
export const SET_BILLING_HISTORY = "actions/account-settings/set-billing-history";
export const SET_PAGINATION_LIMIT = "actions/account-settings/set-pagination-limit";
export const NEXT_PAGE = "actions/account-settings/next-page";
export const PREVIOUS_PAGE = "actions/account-settings/previous-page";
export const SET_BILLING_DETAILS = "actions/account-settings/set-billing-details";
export const UPDATE_BILLING_DETAILS = "actions/account-settings/update-billing-details";
export const SET_ACCOUNT_USERS = "actions/account-settings/set-account-users";
export const USER_REMOVED = "actions/account-settings/user-removed";
export const USER_CREATED = "actions/account-settings/user-created";
export const SET_INVITE_USER_VISIBILITY = "actions/account-settings/set-invite-user-visibility";
export const NO_BILLING_DETAILS = "actions/account-settings/no-billing-details";
export const SET_REMOVE_SELF_VISIBILTIY = "actions/account-settings/set-remove-self-visibility";
export const SET_DOC_RETURN_STATUS = "actions/account-settings/set-doc-return-status";
export const CHANGE_PLAN_LOADING = "actions/account-settings/change-plan-loading";
export const CHANGE_PLAN_LOADING_STOP = "actions/account-settings/change-plan-loading-stop";
export const SET_PENDING_PLAN = "actions/account-settings/set-pending-plan";
export const SET_CANCEL_SUBSCRIPTION_VISIBILITY = "actions/account-settings/set-cancel-subscription-visibility";
export const SHOW_UPGRADE_DOWNGRADE_MODAL = "actions/account-settings/show-upgrade-downgrade-modal";
export const HIDE_UPGRADE_DOWNGRADE_MODAL = "actions/account-settings/hide-upgrade-downgrade-modal";
export const UPDATE_ACCOUNT_NAME_AND_EMAIL = "actions/account-settings/update-account-name-and-email";
export const UPDATE_ADDRESS = "actions/account-settings/update-address";
export const UPDATE_CLT_SETTINGS = "actions/account-settings/update-clt-settings";
export const REDIRECT_TO_BILLING = "actions/account-settings/redirect-to-billing";
export const DOCUMENT_RETURN_LOADING = "actions/account-settings/documen-return-loading";
export const DOCUMENT_RETURN_LOADING_STOP = "actions/account-settings/documen-return-loading-stop";

export const fetchAccountDetailsAction = (accountId) => (dispatch) => {
  fetchAccountDetails(accountId)
    .then((data) => {
      dispatch({ type: SET_ACCOUNT_DETAILS, data });
    });
};

export const fetchAccountUsersAction = (accountId) => (dispatch) => {
  fetchAccountUsers(accountId)
    .then(({ maxUsers, users }) => {
      dispatch({
        type: SET_ACCOUNT_USERS,
        data: { maxUsers, users }
      });
    });
};

export const fetchLocalesAction = (accountId) => (dispatch) => {
  fetchLocales(accountId)
    .then(({ supportedLocales }) => {
      dispatch({
        type: SET_LOCALES,
        data: { supportedLocales }
      });
    });
};

export const saveAccountSettingsAction = () => (dispatch) => {
  const state = store.getState();
  const accountId = state.app.selectedAccount.id;
  const form = R.path(["form", "accountNameAndEmailForm", "values"], state);
  const settings = {
    emailPin: form.emailPin,
    slug: form.slug,
    label: form.label
  };
  return putAccountSettings(accountId, settings)
    .then(() => {
      dispatch({
        type: UPDATE_ACCOUNT_NAME_AND_EMAIL,
        data: { settings }
      });
      dispatch(setFlashMessageAction("Changes Saved"));
    })
    .catch((error) => {
      dispatch(setFlashMessageAction(error.response.data.reason, "error"));
    });
};

export const saveAddressAction = () => (dispatch) => {
  const state = store.getState();
  const accountId = state.app.selectedAccount.id;
  const address = R.path(["form", "mailInSettingsForm", "values"], state);
  const newAddress = R.set(R.lensProp("country"), address.country.name, address);
  return putAccountSettings(accountId, { address: newAddress })
    .then(() => {
      dispatch({
        type: UPDATE_ADDRESS,
        data: { newAddress }
      });
      dispatch(setFlashMessageAction("Changes Saved"));
    });
};

export const setPlanTypeDisplayAction = (type) => ({
  type: SET_PLAN_TYPE_DISPLAY,
  data: { type }
});

export const fetchPlansAction = (accountId) => (dispatch) => {
  fetchPlans(accountId)
    .then(({ plans }) => {
      dispatch({
        type: SET_PLANS,
        data: { plans }
      });
    })
    .then(() => dispatch(fetchPendingPlanAction(accountId)));
};

export const fetchBillingHistoryAction = (accountId) => (dispatch) => {
  fetchBillingHistory(accountId)
    .then(({ invoices }) => {
      dispatch({
        type: SET_BILLING_HISTORY,
        data: { invoices }
      });
      /* This is a temporary solution to solve an issue where the width of the data grid (react-data-grid) is
       incorrect when a small number of rows are on display */
      window.dispatchEvent(new Event("resize"));
    });
};

export const setPaginationLimitAction = (limit) => ({
  type: SET_PAGINATION_LIMIT,
  data: { limit }
});

export const nextPageAction = () => ({
  type: NEXT_PAGE
});

export const previousPageAction = () => ({
  type: PREVIOUS_PAGE
});

export const fetchBillingDetailsAction = (accountId) => (dispatch) => {
  fetchBillingDetails(accountId)
    .then(({ billingDetails }) => {
      dispatch({
        type: SET_BILLING_DETAILS,
        data: { billingDetails }
      });
    })
    .catch((error) => {
      if (error.response.status !== 404) {
        return dispatch(setFlashGenericErrorAction());
      }
      return dispatch({ type: NO_BILLING_DETAILS });
    });
};

export const updatePlanAction = (accountId, plan) => (dispatch) => {
  dispatch({ type: CHANGE_PLAN_LOADING });
  const newPlan = { planType: plan.planType, subscriptionType: plan.subscriptionType };
  putNewPlan(accountId, newPlan)
    .then(() => {
      dispatch({
        type: UPDATE_PLAN,
        data: { plan }
      });
    })
    .then(() => dispatch(fetchPendingPlanAction(accountId)))
    .then(() => dispatch(setFlashMessageAction("Account plan updated")))
    .then(() => dispatch({ type: CHANGE_PLAN_LOADING_STOP }))
    .then(() => dispatch(hideUpgradeDowngradeModalAction()))
    .catch(() => dispatch({ type: CHANGE_PLAN_LOADING_STOP }));
};

export const updateBillingDetailsAction = (field, payload) => ({
  type: UPDATE_BILLING_DETAILS,
  data: { field, payload }
});

export const saveBillingDetailsAction = (accountId, billingDetails) => (dispatch) => {
  postBillingDetails(accountId, billingDetails)
    .then(() => {
      dispatch(setFlashMessageAction("Credit card saved"));
    })
    .catch(() => {
      dispatch(setFlashGenericErrorAction());
    });
};

export const saveCurrencyLocaleTaxAction = () => (dispatch) => {
  const state = store.getState();
  const accountId = state.app.selectedAccount.id;
  const settings = R.path(["form", "currencyTaxLanguageForm", "values"], state);
  const newSettings = {
    currency: settings.currency.key,
    locale: settings.locale.key,
    showUniversalCategorySet: settings.isDefaultCategories,
    vat: settings.vat.toString()
  };
  return putCurrencyLocaleTax(accountId, newSettings)
    .then(() => {
      dispatch({
        type: UPDATE_CLT_SETTINGS,
        data: { newSettings }
      });
      dispatch(setFlashMessageAction("Changes Saved"));
    })
    .catch(() => dispatch(setFlashGenericErrorAction()));
};

export const removeUserAction = (accountId, userId) => (dispatch) => {
  const state = store.getState();
  const currentUserId = state.app.user.id;

  return deleteUser(accountId, userId)
    .then(() => {
      dispatch({
        type: USER_REMOVED,
        data: { userId }
      });
    })
    .then(() => {
      if (userId === currentUserId) {
        return dispatch(redirectToLoginAction());
      }
      return dispatch(setFlashMessageAction("User Removed", "error"));
    })
    .catch(() => dispatch(setFlashGenericErrorAction()));
};

export const createUserAction = () => (dispatch) => {
  const state = store.getState();
  const accountId = state.app.selectedAccount.id;
  const user = R.path(["form", "inviteUserForm", "values"], state);

  if (user && isValidUser(user)) {
    return createUser(accountId, user)
      .then(({ newUser }) => {
        dispatch({
          type: USER_CREATED,
          data: { newUser }
        });
      })
      .then(() => {
        dispatch(setFlashMessageAction("Invitation Sent!"));
      })
      .catch(() => {
        dispatch(setFlashGenericErrorAction());
      });
  }
  return dispatch(setFlashMessageAction("Please check that the email and full name is entered properly.", "error"));
};

export const setInviteUserVisibilityAction = (visible) => ({
  type: SET_INVITE_USER_VISIBILITY,
  data: { visible }
});

export const setRemoveSelfVisibilityAction = (visible) => ({
  type: SET_REMOVE_SELF_VISIBILTIY,
  data: { visible }
});

export const updateDocReturnAction = (accountId, isOn) => (dispatch) => {
  dispatch({ type: DOCUMENT_RETURN_LOADING });
  return putDocReturn(accountId, isOn)
    .then(() => {
      if (isOn) {
        dispatch(setFlashMessageAction("Document Return Enabled"));
      }
      dispatch({
        type: SET_DOC_RETURN_STATUS,
        data: { isOn }
      });
      dispatch({ type: DOCUMENT_RETURN_LOADING_STOP });
    })
    .catch(() => {
      dispatch(setFlashGenericErrorAction());
      dispatch({ type: DOCUMENT_RETURN_LOADING_STOP });
    });
};

export const fetchPendingPlanAction = (accountId) => (dispatch) => (
  fetchPendingPlan(accountId)
    .then(({ cancelDate, pendingPlan }) => {
      dispatch({
        type: SET_PENDING_PLAN,
        data: { cancelDate, pendingPlan }
      });
    })
);

export const setCancelSubscriptionVisibilityAction = (visible) => ({
  type: SET_CANCEL_SUBSCRIPTION_VISIBILITY,
  data: { visible }
});

export const showUpgradeDowngradeModalAction = (plan) => ({
  type: SHOW_UPGRADE_DOWNGRADE_MODAL,
  data: { plan }
});

export const hideUpgradeDowngradeModalAction = () => ({
  type: HIDE_UPGRADE_DOWNGRADE_MODAL
});

export const redirectToBillingAction = (accountId) => (dispatch) => (
  billingRedirect(accountId)
    .then(() => {
      dispatch({
        type: "NOOP"
      });
    })
);



// WEBPACK FOOTER //
// ./src/js/actions/account-settings-actions.js